/** API Feedback — what assistants and scripts ran into while using the API.
 *
 * Mirrors Wheeler Change Control's: unclear documentation, confusing errors, things the API
 * cannot do. The assistant briefing tells them to report it, so this is a to-do list rather
 * than a complaints box. An empty queue means they have not needed to — which is the point.
 */

import { useState } from 'react'

import { Button, EmptyState, ErrorState, Loading, Pill, inputClass } from '@/components/ui'
import {
  type ApiFeedbackItem,
  type FeedbackFilters,
  useApiFeedback,
  useResolveApiFeedback,
} from '@/features/api-access/useApiAccess'

const CATEGORIES = [
  'documentation',
  'error_message',
  'missing_capability',
  'unexpected_behaviour',
  'other',
] as const
const SEVERITIES = ['high', 'medium', 'low'] as const
const PAGE = 25

const label = (value: string) => value.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())

export function ApiFeedbackPage() {
  const [status, setStatus] = useState<'unresolved' | 'resolved' | 'all'>('unresolved')
  const [category, setCategory] = useState('')
  const [severity, setSeverity] = useState('')
  const [offset, setOffset] = useState(0)
  const filters: FeedbackFilters = {
    resolved: status === 'all' ? undefined : status === 'resolved',
    category: category || undefined,
    severity: severity || undefined,
    offset,
  }
  const { data, isPending, error } = useApiFeedback(filters)
  const reset = (apply: () => void) => {
    apply()
    setOffset(0)
  }

  return (
    <div className="mx-auto max-w-[1000px] pt-2">
      <header className="mb-6">
        <h1 className="font-heading text-3xl text-fg">API Feedback</h1>
        <p className="mt-1 max-w-[75ch] text-sm text-fg-muted">
          What assistants and scripts ran into while using the API — unclear documentation,
          confusing errors, things it cannot do. They are told to report it, so this is a to-do
          list rather than a complaints box.
        </p>
      </header>

      <div className="card mb-6 grid gap-3 p-4 sm:grid-cols-3">
        <label className="text-sm">
          <span className="micro-label text-fg-muted">Status</span>
          <select
            className={inputClass}
            value={status}
            onChange={(e) => reset(() => setStatus(e.target.value as typeof status))}
          >
            <option value="unresolved">Unresolved</option>
            <option value="resolved">Resolved</option>
            <option value="all">All</option>
          </select>
        </label>
        <label className="text-sm">
          <span className="micro-label text-fg-muted">Category</span>
          <select
            className={inputClass}
            value={category}
            onChange={(e) => reset(() => setCategory(e.target.value))}
          >
            <option value="">Any</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {label(c)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="micro-label text-fg-muted">Severity</span>
          <select
            className={inputClass}
            value={severity}
            onChange={(e) => reset(() => setSeverity(e.target.value))}
          >
            <option value="">Any</option>
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {label(s)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {isPending ? <Loading /> : null}
      {error ? <ErrorState error={error} /> : null}
      {data ? (
        data.items.length === 0 ? (
          <EmptyState title="Nothing matches those filters.">
            {status === 'unresolved' && !category && !severity
              ? 'Assistants are told to report anything about the API that is unclear or harder than it should be. An empty queue means they have not needed to — which is the point.'
              : 'Widen the filters to see the rest.'}
          </EmptyState>
        ) : (
          <>
            <div className="space-y-3">
              {data.items.map((item) => (
                <FeedbackCard key={item.id} item={item} />
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between text-sm text-fg-muted">
              <span>
                Showing {offset + 1}–{offset + data.items.length} of {data.total} ·{' '}
                {data.unresolved} unresolved
              </span>
              <div className="flex gap-2">
                <Button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
                  Previous
                </Button>
                <Button
                  disabled={offset + data.items.length >= data.total}
                  onClick={() => setOffset(offset + PAGE)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )
      ) : null}
    </div>
  )
}

function FeedbackCard({ item }: { item: ApiFeedbackItem }) {
  const resolve = useResolveApiFeedback()
  const [note, setNote] = useState(item.resolutionNote)
  const tone = item.severity === 'high' ? 'red' : item.severity === 'medium' ? 'amber' : 'neutral'
  return (
    <div className="card p-4">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <Pill tone={tone}>{item.severity}</Pill>
        <Pill>{label(item.category)}</Pill>
        {item.resolved ? <Pill tone="green">resolved</Pill> : null}
        <span className="text-xs text-fg-muted">
          {new Date(item.reportedAt).toLocaleString()} · {item.reportedBy}
          {item.credential ? ` via ${item.credential}` : ''}
        </span>
      </div>
      <p className="font-semibold text-fg">{item.summary}</p>
      {item.endpoint ? (
        <p className="mt-1 text-xs">
          <code>{item.endpoint}</code>
        </p>
      ) : null}
      {item.detail ? (
        <p className="mt-2 whitespace-pre-wrap text-sm text-fg-muted">{item.detail}</p>
      ) : null}
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <input
          className={`${inputClass} max-w-xl flex-1`}
          placeholder="Fixed the 422 message, or: expected behaviour, documented in the briefing."
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <Button
          variant={item.resolved ? 'secondary' : 'primary'}
          disabled={resolve.isPending}
          onClick={() => resolve.mutate({ id: item.id, resolved: !item.resolved, note })}
        >
          {item.resolved ? 'Reopen' : 'Mark resolved'}
        </Button>
      </div>
      {item.resolved && item.resolvedBy ? (
        <p className="mt-2 text-xs text-fg-muted">
          Resolved by {item.resolvedBy} {item.resolvedAt ? `on ${new Date(item.resolvedAt).toLocaleDateString()}` : ''}
        </p>
      ) : null}
      {resolve.error ? <ErrorState error={resolve.error} /> : null}
    </div>
  )
}
